import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { instance } from "@/lib/axios/instance";

type DownloadInvoiceCsvParams = {
  apiEndpointBase: string;
  entityId: string;
  year: string;
  month: string;
  tenantId?: string | null;
  /** Override the default URL path. When provided, apiEndpointBase and entityId are ignored. */
  urlPath?: string;
  /** Additional query params beyond tenantId. */
  extraParams?: Record<string, string | boolean | undefined>;
};

const INVALID_FILENAME_CHARS = /[\\/:*?"<>|]/g;

function getFileNameFromContentDisposition(
  contentDisposition: string | null,
): string | null {
  if (!contentDisposition) return null;
  const filenameMatch = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(
    contentDisposition,
  );
  return filenameMatch ? filenameMatch[1].trim() : null;
}

function sanitizeFileName(name: string): string {
  return name.replaceAll(INVALID_FILENAME_CHARS, "_");
}

/**
 * Downloads invoice CSV file for a specific entity, year, and month.
 * Uses filename from Content-Disposition when available; otherwise falls back to invoices-{year}-{month}.csv.
 */
export function useDownloadInvoiceCsv() {
  return useMutation<void, AxiosError, DownloadInvoiceCsvParams>({
    mutationFn: async ({
      apiEndpointBase,
      entityId,
      year,
      month,
      tenantId,
      urlPath,
      extraParams,
    }) => {
      const url =
        urlPath ??
        `/api/v2/${apiEndpointBase}/${entityId}/Invoices/${year}/${month}/Download`;
      const response = await instance.get(url, {
        params: {
          tenantId: tenantId || undefined,
          ...extraParams,
        },
        responseType: "blob",
      });

      const contentDisposition =
        response.headers["content-disposition"] ?? null;
      const serverFileName =
        getFileNameFromContentDisposition(contentDisposition);
      const downloadFileName =
        serverFileName && serverFileName.length > 0
          ? sanitizeFileName(serverFileName)
          : `invoices-${year}-${month}.csv`;

      const blob = new Blob([response.data], {
        type: "text/csv;charset=utf-8;",
      });

      const blobUrl = globalThis.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", downloadFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(blobUrl);
    },
  });
}
